import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './DragDropSelect.scss'

interface DragDropSelectProps {
  title: string
  items: { id: string; name: string }[]
  value: string[]
  onChange: (value: string[]) => void
  emptyText?: string
  className?: string
  disabled?: boolean
}

export const DragDropSelect: React.FC<DragDropSelectProps> = ({ title, items, value = [], onChange, emptyText, className, disabled = false }) => {
  const { t } = useTranslation()
  const [selectedItems, setSelectedItems] = useState<{ id: string; name: string }[]>([])
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null)

  useEffect(() => {
    const selected = value.map((id) => items.find((item) => item.id === id)).filter((item) => item) as { id: string; name: string }[]
    setSelectedItems(selected)
  }, [value, items])

  const unselectedItems = items.filter((item) => !value.includes(item.id))

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    setDraggedItem(itemId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDragOverTarget(null)
  }

  const handleDragOver = (e: React.DragEvent, target: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverTarget(target)
  }

  const handleDrop = (e: React.DragEvent, targetList: 'selected' | 'unselected') => {
    e.preventDefault()

    if (!draggedItem) return

    const isCurrentlySelected = value.includes(draggedItem)

    if (targetList === 'selected' && !isCurrentlySelected) {
      // Moving from unselected to selected
      onChange([...value, draggedItem])
    } else if (targetList === 'unselected' && isCurrentlySelected) {
      // Moving from selected to unselected
      onChange(value.filter((id) => id !== draggedItem))
    }

    setDraggedItem(null)
    setDragOverTarget(null)
  }

  const handleClick = (itemId: string) => {
    if (disabled) return
    const isSelected = value.includes(itemId)
    if (isSelected) {
      onChange(value.filter((id) => id !== itemId))
    } else {
      onChange([...value, itemId])
    }
  }

  return (
    <div className={`drag-drop-select ${className || ''}`}>
      <div className="d-flex flex-row mt-2 align-items-center">
        <span className="me-2 drag-drop-select__title">{title}:</span>
      </div>

      <div className="mt-2">
        <div
          className={`d-flex flex-wrap flex-row gap-2 w-100 selected-list ${dragOverTarget === 'selected' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'selected')}
          onDrop={(e) => handleDrop(e, 'selected')}
        >
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className={`badge drag-drop-select__badge drag-drop-select__badge--selected ${draggedItem === item.id ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, item.id)}
              onDragEnd={handleDragEnd}
              onClick={() => handleClick(item.id)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              {item.name}
            </div>
          ))}
          {selectedItems.length === 0 && (
            <div className="badge drag-drop-select__badge drag-drop-select__badge--placeholder drag-drop-select__empty">
              {emptyText || t('no_items_selected')}
            </div>
          )}
        </div>
      </div>

      <div className="mt-2">
        <div
          className={`d-flex flex-wrap flex-row gap-2 w-100 unselected-list ${dragOverTarget === 'unselected' ? 'drag-over' : ''}`}
          onDragOver={(e) => handleDragOver(e, 'unselected')}
          onDrop={(e) => handleDrop(e, 'unselected')}
        >
          {unselectedItems.map((item) => (
            <div
              key={item.id}
              className={`badge drag-drop-select__badge drag-drop-select__badge--unselected ${draggedItem === item.id ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, item.id)}
              onDragEnd={handleDragEnd}
              onClick={() => handleClick(item.id)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              {item.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
